import React, { useEffect, useRef, useState } from 'react';
import { classes, st } from '../TableHeader.st.css';
import { IconTooltip, TextButton, TextButtonPriority } from 'wix-ui-tpa';
import { Sort, ChevronDown } from '@wix/wix-ui-icons-common/on-stage';
import ActionsMenuLayoutComponent from './ActionsMenuLayoutComponent';
import { isDisableTableHeader } from '../../../../../../Shared/accessModalsHelper';
import useObservable from '../../../../../../../Shared/Hooks/useObservable.Hook';
import { getRootFolder } from '../../../../../../Services/RootFolder.service';
import { getSelectedFolder } from '../../../../../../Services/SelectedFolder.service';
import { getSort, setSort } from '../../../../../../Services/Sort.service';
import { getIsServerError } from '../../../../../../Services/ServerError.service';
import { useTranslation } from '@wix/yoshi-flow-editor';
import {
  getAppSettings,
  getSettingsData,
} from '../../../../../../Services/AppSettings.service';
import { getSortLabels } from './helpers';
import {
  LibraryItemSortBy,
  TextAlignment,
} from '@wix/ambassador-file-sharing-v1-settings/types';

const SortWrapper = () => {
  const container: any = useRef();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [, rootFolder]: any = useObservable(getRootFolder());
  const [, selectedFolder]: any = useObservable(getSelectedFolder());
  const [, sortObj]: any = useObservable(getSort());
  const [, serverError]: any = useObservable(getIsServerError());
  const [, AppSettings]: any = useObservable(getAppSettings());
  const [, settingsData]: any = useObservable(getSettingsData());
  const isMobile = AppSettings !== undefined ? AppSettings?.isMobile : '';
  const isMembersArea = AppSettings?.isMembersArea ?? false;
  const sortLabels = getSortLabels(t);
  const sortByLabel = sortLabels[sortObj?.sortBy as LibraryItemSortBy];
  const rtlStatus = settingsData?.settings?.displaySettings?.textAlignment;
  const isRTL = rtlStatus === TextAlignment.RIGHT;

  useEffect(() => {
    if (
      settingsData?.settings?.displaySettings &&
      (sortObj?.sortBy === '' ||
        (AppSettings?.isEditor && !AppSettings?.isPreview))
    ) {
      const { defaultSort, defaultOrientation } =
        settingsData?.settings?.displaySettings;
      setSort({ sortBy: defaultSort, orientation: defaultOrientation });
    }
  }, [
    AppSettings?.isEditor,
    AppSettings?.isPreview,
    settingsData?.settings?.displaySettings,
    sortObj?.sortBy,
  ]);
  const disabledTableHeader = isDisableTableHeader({
    rootFolder,
    selectedFolder,
    serverError,
  });
  const handleOpenDropDown = (state: boolean) => {
    setIsOpen(!state);
  };
  const handleClickOutside = (event: any) => {
    if (container.current && !container.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  const handleOnKeyDown = (event: any) => {
    const enterBarKey = 'Enter';
    if (event.key === 'Escape') {
      handleOpenDropDown(true);
    }
    if (event.key === enterBarKey) {
      handleOpenDropDown(isOpen);
    }
  };

  useEffect(() => {
    const sortItem = document.getElementById('sortContainer');
    sortItem
      ?.querySelector(':nth-child(1) > button')
      ?.setAttribute('tabIndex', '-1');
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div
      className={st(classes.sortMenuContainer, { isMembersArea, isMobile })}
      ref={container}
      id="sortContainer"
      aria-label="sort by menu"
      aria-haspopup="menu"
      aria-expanded={isOpen}
      typeof="button"
      tabIndex={0}
      role="button columnheader"
      onKeyDown={handleOnKeyDown}
    >
      {isMembersArea && isMobile ? (
        <TextButton
          className={classes.sortTextButton}
          priority={TextButtonPriority.secondary}
          onClick={() => handleOpenDropDown(isOpen)}
          suffixIcon={<ChevronDown aria-hidden="true" />}
        >
          {t('widget.members.sort.by', { sortBy: sortByLabel })}
        </TextButton>
      ) : (
        <IconTooltip
          message={t('app.widget.sort.tooltip')}
          showTooltip={showTooltip}
          onMouseEnter={() =>
            isMobile !== undefined && !isMobile ? setShowTooltip(true) : null
          }
          onMouseLeave={() => setShowTooltip(false)}
          disabled={disabledTableHeader}
          icon={
            <Sort
              className={
                classes.sortIcon +
                ' ' +
                (disabledTableHeader ? classes.disabledSortIcon : '')
              }
              aria-hidden="true"
            />
          }
          onClick={() => handleOpenDropDown(isOpen)}
        />
      )}
      {isOpen && !disabledTableHeader && (
        <div
          className={st(classes.sortMenu, { isMembersArea, isMobile, isRTL })}
          onKeyDown={handleOnKeyDown}
        >
          <ActionsMenuLayoutComponent
            isMobile={isMobile}
            orientation={sortObj.orientation}
            sortBy={sortObj.sortBy}
            setIsOpen={setIsOpen}
            isMembersArea={isMembersArea}
          />
        </div>
      )}
    </div>
  );
};

export default SortWrapper;
